'use client';

import { Play, Pause, Volume2, VolumeX, Maximize, PictureInPicture2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import type { VideoPlayerHandle, VideoPlayerState } from '@/app/_components/shared/video/video.types';
import { cn } from '@/app/_lib/utils';

interface VideoControlsProps {
  playerHandle: VideoPlayerHandle;
  playerState: VideoPlayerState;
  videoType?: 'video' | 'youtube';
  className?: string;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatRemaining(current: number, duration: number): string {
  const remaining = Math.max(0, duration - current);
  return `-${formatTime(remaining)}`;
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const ICON_BTN = 'flex size-7 shrink-0 cursor-pointer items-center justify-center text-white/80 transition-colors hover:text-white';

export function VideoControls({ playerHandle, playerState, videoType, className }: VideoControlsProps) {
  const { isPlaying, currentTime, duration, volume, isMuted, isReady, playbackRate } = playerState;
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      playerHandle.seek(percent * duration);
      playerHandle.play();
    },
    [playerHandle, duration]
  );

  const handleVolumeChange = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      playerHandle.setVolume(percent);
      if (percent > 0) playerHandle.setMuted(false);
    },
    [playerHandle]
  );

  if (!isReady) return null;

  return (
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events -- the onClick is pointer-only stopPropagation plumbing (see below); there is no keyboard action to bind, the real controls are the focusable buttons inside. */
    <div
      role="toolbar"
      aria-label="Video controls"
      className={cn('flex w-full items-center gap-1.5 bg-linear-to-t from-black/60 to-transparent px-3 pb-3 pt-8 text-white', className)}
      // Pointer-only plumbing: stop clicks on the bar from bubbling to the video
      // (which toggles play). No keyboard equivalent needed — the controls are
      // the focusable buttons inside this toolbar.
      onClick={(e) => { e.stopPropagation(); }}
    >
      {/* Play/Pause */}
      <button type="button" onClick={() => { if (isPlaying) { playerHandle.pause(); } else { playerHandle.play(); } }} className={ICON_BTN} aria-label={isPlaying ? 'Pause' : 'Play'}>
        {isPlaying ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
      </button>

      {/* Volume + slider */}
      <div
        className="relative flex items-center"
        onMouseEnter={() => { setShowVolumeSlider(true); }}
        onMouseLeave={() => { setShowVolumeSlider(false); }}
        onFocus={() => { setShowVolumeSlider(true); }}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowVolumeSlider(false); }}
      >
        <button
          type="button"
          onClick={() => { playerHandle.setMuted(!isMuted); }}
          className={ICON_BTN}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted || volume === 0 ? <VolumeX className="size-3.5" /> : <Volume2 className="size-3.5" />}
        </button>
        <div className={cn(
          'flex items-center overflow-hidden transition-all duration-200',
          showVolumeSlider ? 'w-16 opacity-100' : 'w-0 opacity-0'
        )}>
          <div
            className="relative flex h-7 w-16 cursor-pointer items-center"
            onClick={handleVolumeChange}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') playerHandle.setVolume(Math.min(1, volume + 0.1));
              if (e.key === 'ArrowLeft') playerHandle.setVolume(Math.max(0, volume - 0.1));
            }}
            role="slider"
            aria-label="Volume"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(isMuted ? 0 : volume * 100)}
            tabIndex={0}
          >
            <div className="h-0.5 w-full bg-overlay-glass-strong">
              <div
                className="h-full bg-white"
                style={{ width: `${isMuted ? 0 : volume * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Current time */}
      <span className="shrink-0 text-xs tabular-nums text-white/70">
        {formatTime(currentTime)}
      </span>

      {/* Seekbar */}
      <div
        className="relative flex h-7 flex-1 cursor-pointer items-center"
        onClick={handleSeek}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') playerHandle.seek(Math.min(duration, currentTime + 5));
          if (e.key === 'ArrowLeft') playerHandle.seek(Math.max(0, currentTime - 5));
        }}
        role="slider"
        aria-label="Video progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
        tabIndex={0}
      >
        <div className="h-0.5 w-full bg-overlay-glass-strong">
          <div
            className="h-full bg-white transition-[width] duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Remaining time */}
      <span className="shrink-0 text-xs tabular-nums text-white/70">
        {formatRemaining(currentTime, duration)}
      </span>

      {/* Speed */}
      <div className="relative">
        <button
          type="button"
          onClick={() => { setShowSpeedMenu((prev) => !prev); }}
          className={cn(ICON_BTN, 'text-xs font-medium tabular-nums')}
          aria-label="Playback speed"
          aria-expanded={showSpeedMenu}
          aria-haspopup="menu"
        >
          {playbackRate}x
        </button>
        {showSpeedMenu && (
          <div
            className="absolute bottom-full right-0 mb-2 flex flex-col rounded-md bg-overlay-surface py-1 backdrop-blur-md"
            role="menu"
            tabIndex={-1}
            onKeyDown={(e) => { if (e.key === 'Escape') setShowSpeedMenu(false); }}
          >
            {SPEEDS.map((speed) => (
              <button
                key={speed}
                type="button"
                role="menuitem"
                onClick={() => { playerHandle.setPlaybackRate(speed); setShowSpeedMenu(false); }}
                className={cn(
                  'cursor-pointer px-3 py-1 text-xs tabular-nums transition-colors hover:bg-overlay-glass',
                  playbackRate === speed ? 'text-white' : 'text-white/60'
                )}
              >
                {speed}x
              </button>
            ))}
          </div>
        )}
      </div>

      {/* PiP - only for self-hosted (not supported in YouTube iframes) */}
      {videoType !== 'youtube' && (
        <button type="button" onClick={() => { void playerHandle.requestPip(); }} className={ICON_BTN} aria-label="Picture in Picture">
          <PictureInPicture2 className="size-3.5" />
        </button>
      )}

      {/* Fullscreen */}
      {playerHandle.supportsFullscreen && (
        <button type="button" onClick={() => { playerHandle.requestFullscreen(); }} className={ICON_BTN} aria-label="Fullscreen">
          <Maximize className="size-3.5" />
        </button>
      )}
    </div>
  );
}
